import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'October 23, 2026';

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-semibold text-foreground mb-3">{title}</h2>
      <div className="space-y-3 text-foreground/90 leading-relaxed">{children}</div>
    </section>
  );
}

function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-10 md:py-16 pb-20 md:pb-16">
        <h1 className="text-3xl font-bold text-foreground mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="bg-secondary/50 border border-border rounded-lg p-4 mb-8">
          <p className="text-sm text-foreground">
            <strong>Beta Notice:</strong> Laftel Condo is currently in beta. Data handling
            practices described here reflect the current state of the Service and may change as
            the product develops; we will update this page when they do.
          </p>
        </div>

        <Section title="1. Who We Are">
          <p>
            This Privacy Policy explains how Laftel Inc. ("Laftel", "we", "us") collects, uses,
            and shares information when you use Laftel Condo (the "Service"), a software
            platform for residents, owners, tenants, council members, and managers of a
            participating condo community to communicate and stay informed. Laftel provides the
            software only and is not the condo community, property manager, or building itself.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We collect the following categories of information:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account information:</strong> name, email address, role, and unit/condo association. There are no passwords: you sign in with a one-time code emailed to you.</li>
            <li><strong>Content you submit:</strong> bulletins, forum posts and comments, tickets and ticket updates, feedback, amenity bookings, and any images or documents you upload.</li>
            <li><strong>Notification preferences:</strong> which types of email notifications you have opted in or out of.</li>
            <li><strong>Usage and technical data:</strong> log data such as request metadata, user agent, and error reports generated by the Service.</li>
            <li><strong>Communications:</strong> emails we send you (e.g. bulletins, ticket updates, approval notices) and, where applicable, emails you send that are processed into tickets or bulletins.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Operate core features: bulletins, forum, tickets, calendar, handbook, and amenity booking.</li>
            <li>Send email notifications you have opted into (e.g. new bulletins, forum activity, ticket updates, amenity bookings) and account-related emails (approval, sign-in codes).</li>
            <li>Authenticate your account and enforce access based on your condo, unit, and role.</li>
            <li>Use AI (OpenAI) to help process incoming emails into tickets/bulletins and to summarize or polish admin-authored content.</li>
            <li>Maintain and improve the Service, including diagnosing bugs during this beta period.</li>
          </ul>
        </Section>

        <Section title="4. How We Share Information">
          <p>
            We do not sell your personal information. We share information only as necessary to
            operate the Service, including with:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Mailgun</strong> — to deliver transactional and notification emails.</li>
            <li><strong>OpenAI</strong> — to process email/forum/bulletin content for automated ticket creation and content assistance.</li>
            <li>Administrators of your condo community, who can see information relevant to managing that community (e.g. resident lists, tickets, pending registrations).</li>
            <li>Other residents, where content is intentionally shared (e.g. forum posts, bulletins) as part of using the Service.</li>
          </ul>
          <p>
            We may also disclose information if required by law or to protect the rights,
            safety, or property of Laftel, its users, or others.
          </p>
        </Section>

        <Section title="5. Data Retention">
          <p>
            We retain account and content data for as long as your account is active or as
            needed to provide the Service. You may request deletion of your account and
            associated personal data at any time, subject to any content that must be retained
            for legitimate operational or legal reasons (e.g. historical ticket records relevant
            to the condo community).
          </p>
        </Section>

        <Section title="6. Your Choices">
          <p>
            You can manage your email notification preferences at any time from your account
            settings, or by using the unsubscribe link included in the footer of every
            notification email — no login required. You can also contact us to request access
            to, correction of, or deletion of your personal information.
          </p>
        </Section>

        <Section title="7. Cookies, Local Storage and Analytics">
          <p>
            The Service uses browser local storage to keep you signed in (storing your
            authentication token) and does not use third-party advertising or tracking cookies.
          </p>
          <p>
            We use our own first-party analytics, with no third-party trackers, to understand how
            the Service is used: pages visited, time spent, which links and buttons are clicked,
            and key actions such as signing in, posting or commenting on the forum, reacting,
            following a ticket or booking an amenity (counts only, never the content), together
            with the browser, device type, approximate country and whether you are signed in
            (never your account or email). It never
            records what you type or the text of what you click inside a condo, and IP addresses
            are not stored. A random identifier kept in local storage recognizes returning
            visits; visitors from the EU, the UK and Switzerland are counted without it. Browsers
            that send Do Not Track or Global Privacy Control are not tracked.
          </p>
        </Section>

        <Section title="8. Security">
          <p>
            We take reasonable measures to protect your information, including single-use, short-lived sign-in codes
            and access controls scoped to your condo and role. However, no system is completely
            secure, and — as a beta product — the Service may be more likely to contain security
            issues than a mature product. Please report any suspected vulnerability to us
            promptly.
          </p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>
            The Service is intended for adult residents, owners, tenants, and managers of condo
            communities and is not directed at children. We do not knowingly collect personal
            information from children.
          </p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time, particularly as the Service
            evolves out of beta. We will post the updated policy here with a revised "Last
            updated" date.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            Questions about this Privacy Policy or your personal information can be sent to{' '}
            <a href="mailto:privacy@laftel.io" className="text-primary hover:underline">privacy@laftel.io</a>.
            See also our <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>.
          </p>
        </Section>

        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to Home
        </Link>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
